import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import { brand } from "../../../theme/businessFlowsTheme";
import { Refusal } from "../../workspaces/WorkspaceUi";
import { getViewFieldOptions, resetViewFields, saveViewFields } from "../../../actions";
import { attributeLabel } from "../../../utils/viewConfig";
import PropertiesPanelSettings from "../config/PropertiesPanelSettings";
import EmptyState from "./EmptyState";
import SortableList from "./SortableList";

const VIEW_OPTIONS = [
  { key: "card", label: "Search cards", help: "Shown under the name of each advanced search result." },
  { key: "hover", label: "Explorer fact sheet", help: "Shown when hovering an object in the relationship explorer." },
  { key: "quickSearch", label: "Quick search", help: "The first one is the line under each result of the top bar search." },
  { key: "panel", label: "Properties panel", help: "The attributes panel beside diagrams, maps, search and the explorer." },
];

// Per type and place: which attributes are shown, in which order. A place
// nobody configured shows every attribute the objects carry, description first.
const keyOf = (database, view, objectType) => JSON.stringify([database, view, objectType]);
const NO_OPTIONS = { configured: null, effective: [], available: [] };

export default function FieldsTab({ user, overview, database, mayManage, apply, askReset, previewType, onPreview }) {
  const types = useMemo(() => overview.types || [], [overview.types]);
  const [view, setView] = useState("card");
  const [objectType, setObjectType] = useState(previewType || types[0]?.objectType || "");
  // What was read, and for which database, view and type: the draft always
  // belongs to it, so a save never lands on a type chosen since.
  const [loaded, setLoaded] = useState(null);
  const [draft, setDraft] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!types.some((t) => t.objectType === objectType)) setObjectType(types[0]?.objectType || "");
  }, [types, objectType]);

  // Reload when the choice changes, or the saved fields of this view and type
  // do. An answer for an earlier choice arriving late is dropped.
  const savedKey = JSON.stringify(overview.fields?.[view]?.[objectType] || null);
  useEffect(() => {
    if (view === "panel" || !objectType || !database) return undefined;
    let cancelled = false;
    setLoaded(null);
    setError("");
    getViewFieldOptions(database, view, objectType).then((res) => {
      if (cancelled) return;
      const options = res?.success && res.content ? res.content : NO_OPTIONS;
      setLoaded({ key: keyOf(database, view, objectType), database, view, objectType, options });
      setDraft(options.effective || []);
      if (!res?.success) setError(res?.msg || "Couldn't read this type's attributes. Try again in a moment.");
    });
    return () => {
      cancelled = true;
    };
  }, [database, view, objectType, savedKey]);

  if (types.length === 0) {
    return <EmptyState icon={<ViewListRoundedIcon />} title="No object types yet" text="Fields can be chosen once this database holds objects." />;
  }

  const labelOf = (name) => types.find((t) => t.objectType === name)?.label || name;
  const current = VIEW_OPTIONS.find((option) => option.key === view);
  const options = loaded && loaded.key === keyOf(database, view, objectType) ? loaded.options : null;
  const dirty = JSON.stringify(draft) !== JSON.stringify(options?.effective || []);
  const chosen = new Set(draft);
  const available = (options?.available || []).filter((row) => !chosen.has(row.attribute) && row.attribute !== "name" && row.attribute !== "Type");

  const selectType = (value) => {
    setObjectType(value);
    onPreview(value);
  };
  const save = () => {
    if (!options) return undefined;
    const target = loaded;
    const where = VIEW_OPTIONS.find((option) => option.key === target.view);
    return apply(() => saveViewFields(target.database, target.view, target.objectType, draft), `${where.label} for ${labelOf(target.objectType)} saved.`);
  };
  const reset = () =>
    askReset({
      title: `Show every attribute in ${current.label.toLowerCase()}?`,
      text: `${labelOf(objectType)} goes back to showing every attribute its objects carry, description first.`,
      request: () => resetViewFields(database, view, objectType),
      message: `${current.label} for ${labelOf(objectType)} show every attribute again.`,
    });

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
        <TextField
          select
          size="small"
          label="Object type"
          fullWidth={false}
          value={objectType}
          onChange={(e) => selectType(e.target.value)}
          sx={{ width: { xs: "100%", md: 260 }, "& .MuiInputBase-root": { bgcolor: "#fff" } }}
        >
          {types.map((t) => (
            <MenuItem key={t.objectType} value={t.objectType}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: t.colour }} />
                <span>{t.label}</span>
              </Stack>
            </MenuItem>
          ))}
        </TextField>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={(e, value) => value && setView(value)}
          aria-label="Where the fields are shown"
          sx={{ flexWrap: "wrap", bgcolor: "#fff", "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, px: 1.5 } }}
        >
          {VIEW_OPTIONS.map((option) => (
            <ToggleButton key={option.key} value={option.key}>
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {current.help} The name and the type are always the title.
      </Typography>

      {view === "panel" ? (
        <PropertiesPanelSettings key={`${database}-${objectType}`} user={user} database={database} embedded initialType={objectType} viewTypes={types} />
      ) : (
        <>
          {error && <Refusal onClose={() => setError("")}>{error}</Refusal>}
          {options === null ? (
            <Skeleton variant="rounded" height={160} sx={{ borderRadius: 3 }} />
          ) : (
            <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" }, alignItems: "start" }}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: `1px solid ${brand.line}` }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 700 }}>Shown, in this order</Typography>
                  <Chip size="small" label={options.configured ? "Chosen" : "Automatic"} sx={{ height: 20, fontSize: "0.7rem" }} />
                </Stack>
                {draft.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Objects of this type carry no attributes to show yet.
                  </Typography>
                ) : (
                  <SortableList
                    items={draft}
                    getKey={(attribute) => attribute}
                    getLabel={attributeLabel}
                    disabled={!mayManage}
                    onReorder={setDraft}
                    gap={0.75}
                    renderItem={(attribute) => (
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.25, py: 0.75, borderRadius: 2, border: `1px solid ${brand.line}`, bgcolor: "#fff" }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
                          {attributeLabel(attribute)}
                        </Typography>
                        {mayManage && (
                          <Tooltip title="Don't show">
                            <IconButton size="small" aria-label={`Don't show ${attributeLabel(attribute)}`} onClick={() => setDraft(draft.filter((a) => a !== attribute))}>
                              <CloseRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    )}
                  />
                )}
              </Paper>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: `1px solid ${brand.line}` }}>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>Also on these objects</Typography>
                {available.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Every attribute these objects carry is shown.
                  </Typography>
                ) : (
                  <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.75 }}>
                    {available.map((row) =>
                      mayManage ? (
                        <Chip
                          key={row.attribute}
                          icon={<AddRoundedIcon />}
                          label={`${attributeLabel(row.attribute)} · ${row.filled}`}
                          onClick={() => setDraft([...draft, row.attribute])}
                          variant="outlined"
                          sx={{ borderColor: brand.line }}
                        />
                      ) : (
                        <Chip key={row.attribute} label={`${attributeLabel(row.attribute)} · ${row.filled}`} variant="outlined" sx={{ borderColor: brand.line }} />
                      )
                    )}
                  </Stack>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  The number is how many objects hold a value.
                </Typography>
              </Paper>
            </Box>
          )}
          {mayManage && options && (
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
              <Button variant="contained" disabled={!options || !dirty || draft.length === 0} onClick={save}>
                Save fields
              </Button>
              <Button disabled={!dirty} onClick={() => setDraft(options.effective || [])}>
                Undo changes
              </Button>
              {options.configured && (
                <Button startIcon={<RestartAltRoundedIcon />} onClick={reset}>
                  Show every attribute
                </Button>
              )}
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
}
